import type { MfgRecipeLinesMaterialReplaceResponse } from "@/lib/manufacturingClient";

type Props = {
  preview: MfgRecipeLinesMaterialReplaceResponse;
  sourceName: string;
  targetName: string;
  qtyScale: number;
  deactivateSource: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

export function ManufacturingMaterialReplacePreviewModal({
  preview,
  sourceName,
  targetName,
  qtyScale,
  deactivateSource,
  busy,
  onCancel,
  onConfirm,
}: Props) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mfg-material-replace-title"
        className="max-h-[80vh] w-full max-w-lg overflow-auto rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
      >
        <h2 id="mfg-material-replace-title" className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
          Replace on BOMs
        </h2>
        <p className="mt-2 text-xs text-gray-600 dark:text-zinc-300">
          Replace <span className="font-medium">{sourceName}</span> with{" "}
          <span className="font-medium">{targetName}</span>
          {qtyScale !== 1 ? ` (qty × ${qtyScale})` : ""}. Affects{" "}
          <span className="font-medium">{preview.lines}</span> line
          {preview.lines === 1 ? "" : "s"} across{" "}
          <span className="font-medium">{preview.recipes.length}</span> recipe
          {preview.recipes.length === 1 ? "" : "s"}.
          {deactivateSource ? " Source material will be deactivated." : ""}
        </p>
        {preview.recipes.length > 0 ? (
          <ul className="mt-3 max-h-48 space-y-1 overflow-auto rounded border border-gray-100 bg-gray-50 p-2 text-xs dark:border-zinc-700 dark:bg-zinc-800">
            {preview.recipes.map((r) => (
              <li key={r.recipe_id} className="flex justify-between gap-2 text-gray-800 dark:text-zinc-200">
                <span>
                  {r.recipe_code ? `${r.recipe_code} · ` : ""}
                  {r.recipe_name || r.recipe_id}
                </span>
                <span className="shrink-0 text-gray-500 dark:text-zinc-400">
                  {r.line_count} line{r.line_count === 1 ? "" : "s"}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-xs text-amber-800 dark:text-amber-200">
            No recipe lines reference this material.
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-md bg-sky-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onConfirm}
            disabled={busy || preview.lines === 0}
          >
            {busy ? "Applying…" : "Apply replace"}
          </button>
        </div>
      </div>
    </div>
  );
}
